/* ============================================================
   BL Product Videos — single continuous track
   - All videos from all enabled sections sit in ONE track
   - Header arrows scroll to first card of next/prev section
   - Title in header updates as user scrolls past section starts
   - Lazy-load, inline play with sound, no lightbox
   ============================================================ */

(function () {
  'use strict';

  var supportsHls = (function () {
    var v = document.createElement('video');
    return typeof v.canPlayType === 'function' &&
           v.canPlayType('application/vnd.apple.mpegurl') !== '';
  })();

  function safe(fn) { try { fn(); } catch (e) {} }

  function initCarousel(root) {
    if (!root || root.dataset.blPvInit === '1') return;
    root.dataset.blPvInit = '1';

    var showArrows = root.dataset.showArrows === 'true';
    var playWithSound = root.dataset.playWithSound === 'true';

    var track = root.querySelector('[data-bl-pv-track]');
    var items = Array.prototype.slice.call(root.querySelectorAll('.bl-pv__item'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-bl-pv-card]'));
    var videos = cards
      .map(function (c) { return c.querySelector('video.bl-pv__video'); })
      .filter(Boolean);

    var sectionStartItems = items.filter(function (i) {
      return i.dataset.isSectionStart === 'true';
    });

    var sectionPrev = root.querySelector('[data-bl-pv-section-prev]');
    var sectionNext = root.querySelector('[data-bl-pv-section-next]');
    var titleEl = root.querySelector('[data-bl-pv-title]');

    var innerPrev = root.querySelector('[data-bl-pv-inner-prev]');
    var innerNext = root.querySelector('[data-bl-pv-inner-next]');

    var totalSections = sectionStartItems.length;
    var currentSectionIndex = 0;

    /* -------- source loading -------- */

    function loadVideoSource(video) {
      if (!video || video.dataset.loaded === '1') return;
      var hls = video.dataset.hls;
      var mp4 = video.dataset.mp4;

      if (supportsHls && hls) {
        var sHls = document.createElement('source');
        sHls.src = hls;
        sHls.type = 'application/vnd.apple.mpegurl';
        video.appendChild(sHls);
      }
      if (mp4) {
        var sMp4 = document.createElement('source');
        sMp4.src = mp4;
        sMp4.type = 'video/mp4';
        video.appendChild(sMp4);
      }

      video.dataset.loaded = '1';
      safe(function () { video.load(); });
    }

    /* -------- lazy load via IntersectionObserver -------- */

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            loadVideoSource(entries[i].target);
            io.unobserve(entries[i].target);
          }
        }
      }, { rootMargin: '600px 0px', threshold: 0.01 });

      videos.forEach(function (v) { io.observe(v); });
    } else {
      videos.forEach(loadVideoSource);
    }

    /* -------- play helpers -------- */

    function pauseAllExcept(except) {
      videos.forEach(function (v) {
        if (v !== except) {
          safe(function () { v.pause(); });
          var m = v.closest('.bl-pv__media');
          if (m) m.classList.remove('is-playing');
        }
      });
    }

    function startPlay(video, card) {
      loadVideoSource(video);
      pauseAllExcept(video);
      video.muted = !playWithSound;

      var p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(function () {
          card.classList.add('is-playing');
        }).catch(function () {
          // Unmuted play may be blocked by browser policy → fall back to muted
          if (!video.muted) {
            video.muted = true;
            var p2 = video.play();
            if (p2 && typeof p2.then === 'function') {
              p2.then(function () {
                card.classList.add('is-playing');
              }).catch(function () {});
            }
          }
        });
      } else {
        card.classList.add('is-playing');
      }
    }

    /* -------- per-card wiring -------- */

    cards.forEach(function (card) {
      var video = card.querySelector('video.bl-pv__video');
      var playBtn = card.querySelector('[data-bl-pv-play]');
      if (!video || !playBtn) return;

      playBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        startPlay(video, card);
      });

      // Card click outside the play button → toggle pause if playing
      card.addEventListener('click', function (e) {
        if (e.target.closest('[data-bl-pv-play]')) return;
        if (video.paused) {
          startPlay(video, card);
        } else {
          safe(function () { video.pause(); });
          card.classList.remove('is-playing');
        }
      });

      video.addEventListener('play', function () {
        pauseAllExcept(video);
        card.classList.add('is-playing');
      });

      video.addEventListener('pause', function () {
        if (!video.ended) card.classList.remove('is-playing');
      });

      video.addEventListener('ended', function () {
        card.classList.remove('is-playing');
      });
    });

    /* -------- section navigation -------- */

    function scrollToSection(targetIndex) {
      if (targetIndex < 0 || targetIndex >= totalSections) return;
      var targetItem = sectionStartItems[targetIndex];
      if (!targetItem || !track) return;

      // Position relative to the track (avoids scrolling the page itself)
      var trackRect = track.getBoundingClientRect();
      var itemRect = targetItem.getBoundingClientRect();
      var offset = itemRect.left - trackRect.left + track.scrollLeft;

      track.scrollTo({ left: offset, behavior: 'smooth' });
    }

    function detectCurrentSection() {
      if (!track || sectionStartItems.length === 0) return;

      var trackRect = track.getBoundingClientRect();

      // Last section-start whose left edge is at or past the track's left edge
      var newIndex = 0;
      for (var i = 0; i < sectionStartItems.length; i++) {
        var startRect = sectionStartItems[i].getBoundingClientRect();
        if (startRect.left <= trackRect.left + 1) {
          newIndex = i;
        } else {
          break;
        }
      }

      if (newIndex !== currentSectionIndex) {
        currentSectionIndex = newIndex;
        updateTitleAndNav();
      }
    }

    function updateTitleAndNav() {
      // Title always reflects current section
      if (titleEl && sectionStartItems[currentSectionIndex]) {
        titleEl.textContent = sectionStartItems[currentSectionIndex].dataset.sectionTitle || '';
      }

      // Arrows: hidden if admin disabled, only one section, or at the boundary
      if (sectionPrev) {
        var hidePrev = !showArrows ||
                       totalSections < 2 ||
                       currentSectionIndex <= 0;
        if (hidePrev) sectionPrev.setAttribute('hidden', '');
        else sectionPrev.removeAttribute('hidden');
      }
      if (sectionNext) {
        var hideNext = !showArrows ||
                       totalSections < 2 ||
                       currentSectionIndex >= totalSections - 1;
        if (hideNext) sectionNext.setAttribute('hidden', '');
        else sectionNext.removeAttribute('hidden');
      }
    }

    if (sectionPrev) {
      sectionPrev.addEventListener('click', function (e) {
        e.preventDefault();
        scrollToSection(currentSectionIndex - 1);
      });
    }

    if (sectionNext) {
      sectionNext.addEventListener('click', function (e) {
        e.preventDefault();
        scrollToSection(currentSectionIndex + 1);
      });
    }

    /* -------- inner nav arrows (visible on desktop only via CSS @media) -------- */

    function getInnerScrollStep() {
      if (!track) return 200;
      var firstItem = track.querySelector('.bl-pv__item');
      if (!firstItem) return 200;
      var w = firstItem.getBoundingClientRect().width;
      var cs = window.getComputedStyle(track);
      var gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
      return w + gap;
    }

    function updateInnerArrows() {
      if (!track || !innerPrev || !innerNext) return;
      if (!showArrows) {
        innerPrev.setAttribute('hidden', '');
        innerNext.setAttribute('hidden', '');
        return;
      }

      var maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 1) {
        innerPrev.setAttribute('hidden', '');
        innerNext.setAttribute('hidden', '');
        return;
      }

      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft >= maxScroll - 1;

      if (atStart) innerPrev.setAttribute('hidden', '');
      else innerPrev.removeAttribute('hidden');

      if (atEnd) innerNext.setAttribute('hidden', '');
      else innerNext.removeAttribute('hidden');
    }

    if (innerPrev && track) {
      innerPrev.addEventListener('click', function (e) {
        e.preventDefault();
        track.scrollBy({ left: -getInnerScrollStep(), behavior: 'smooth' });
      });
    }

    if (innerNext && track) {
      innerNext.addEventListener('click', function (e) {
        e.preventDefault();
        track.scrollBy({ left: getInnerScrollStep(), behavior: 'smooth' });
      });
    }

    /* -------- scroll listener: detect section changes -------- */

    if (track) {
      var scrollTimer;
      track.addEventListener('scroll', function () {
        if (scrollTimer) clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          detectCurrentSection();
          updateInnerArrows();
        }, 80);
      }, { passive: true });
    }

    /* -------- resize: re-evaluate section state (item positions may shift) -------- */

    var resizeTimer;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        detectCurrentSection();
        updateInnerArrows();
      }, 150);
    });

    /* -------- pause every video when tab loses focus -------- */

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) return;
      videos.forEach(function (v) {
        safe(function () { v.pause(); });
        var m = v.closest('.bl-pv__media');
        if (m) m.classList.remove('is-playing');
      });
    });

    /* -------- initial state -------- */

    updateTitleAndNav();
    updateInnerArrows();
  }

  function initAll(scope) {
    var ctx = scope || document;
    var roots = ctx.querySelectorAll('[data-bl-pv]');
    Array.prototype.forEach.call(roots, initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });
})();
